import { z } from "zod";
import { db, type LocalWifiVoucherIssuance } from "../../core/data/local/db";
import { nowIso } from "../../core/domain/time";
import { uuidv7 } from "../../core/platform/id";
import { DrawerTransportError, type DrawerTransport, type FailureCode } from "./transport";

/**
 * Claiming and pooling WiFi voucher codes (T013, T015, T020 —
 * specs/003-wifi-voucher-tickets, contracts/peripheral-wifi-voucher.md).
 *
 * PRINT-FIRST, THEN COMMIT (research.md R5, FR-306). A code is picked, its
 * ticket is rendered and sent to the transport, and ONLY on success does one
 * Dexie transaction mark it used, record the issuance, and queue the outbox
 * envelope. A failed write leaves the pool untouched — the same code is
 * offered again on the very next tap.
 *
 * SERIALIZED, NOT LOCKED (research.md R6). Exactly one terminal ever claims
 * from this pool (the spec's recorded assumption), so there is no second
 * claimant to race against — but two taps from the SAME barista in quick
 * succession must still never return the same code. `claimWifiVoucher`
 * chains every call onto the previous one so a second claim's "pick the next
 * unused code" always runs after the first claim's commit (or failure), with
 * no persisted job queue needed for it (unlike `ticket-queue.ts`, which
 * exists to protect a background action from a foreground one — there is no
 * background action here to protect).
 */

const poolResponseSchema = z.object({
  vouchers: z.array(
    z.object({
      code: z.string(),
      used: z.boolean(),
      poolBatch: z.string(),
    }),
  ),
});

export const LOW_STOCK_THRESHOLD = 30;

/** Replace the local pool wholesale (research.md R3). Never touches issuance history. */
export async function hydrateWifiVouchers(payload: unknown): Promise<void> {
  const parsed = poolResponseSchema.parse(payload);

  await db.transaction("rw", db.wifiVouchers, async () => {
    await db.wifiVouchers.clear();
    await db.wifiVouchers.bulkAdd(
      parsed.vouchers.map((v) => ({ code: v.code, used: v.used, poolBatch: v.poolBatch })),
    );
  });
}

/**
 * Pull the current pool from the server. A no-op, not an error, when offline
 * or when the response isn't ok — including a missing/expired session, which
 * this endpoint requires (unlike the public `GET /api/catalog`) because a
 * pool listing is 500 codes that grant free internet the moment someone has
 * them. Mirrors `refreshAuthorizers`'s Bearer-token pattern (staff-cache.ts).
 */
export async function refreshWifiVoucherPool(sessionToken: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch("/api/wifi-vouchers", {
      headers: { authorization: `Bearer ${sessionToken}` },
    });
  } catch {
    return;
  }
  if (!response.ok) return;
  await hydrateWifiVouchers(await response.json());
}

export async function countUnusedWifiVouchers(): Promise<number> {
  return db.wifiVouchers.filter((voucher) => !voucher.used).count();
}

async function pickNextUnused(): Promise<{ code: string } | undefined> {
  const unused = await db.wifiVouchers.filter((voucher) => !voucher.used).toArray();
  unused.sort((a, b) => a.code.localeCompare(b.code));
  return unused[0];
}

export type WifiVoucherClaimResult =
  | { outcome: "issued"; code: string }
  | { outcome: "empty" }
  | { outcome: "failed"; failureCode: FailureCode };

export interface WifiVoucherDeps {
  transport: Pick<DrawerTransport, "write">;
  /** Renders the ticket bytes for a claimed code. */
  render: (code: string) => Uint8Array;
  terminalId: () => Promise<string | null> | string | null;
  staffId: () => string;
  now?: () => number;
}

let claimChain: Promise<unknown> = Promise.resolve();

/**
 * Claim one code: print it, and only on success record it as handed out.
 *
 * Exported entry point. Every call is chained onto the previous one so two
 * calls fired without awaiting between them still run their "pick, print,
 * commit" sequences one at a time, never interleaved.
 */
export function claimWifiVoucher(deps: WifiVoucherDeps): Promise<WifiVoucherClaimResult> {
  const run = claimChain.then(() => performClaim(deps));
  // Keep the chain alive even after a rejection — a thrown failure here would
  // otherwise leave every subsequent claim permanently stuck behind it.
  claimChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

async function performClaim(deps: WifiVoucherDeps): Promise<WifiVoucherClaimResult> {
  const candidate = await pickNextUnused();
  if (!candidate) return { outcome: "empty" };

  const bytes = deps.render(candidate.code);

  try {
    await deps.transport.write(bytes);
  } catch (cause) {
    const failureCode: FailureCode =
      cause instanceof DrawerTransportError ? cause.code : "kick_timeout";
    return { outcome: "failed", failureCode };
  }

  const terminalId = (await deps.terminalId()) ?? "";
  const createdAt = deps.now ? new Date(deps.now()).toISOString() : nowIso();
  const issuance: LocalWifiVoucherIssuance = {
    id: uuidv7(),
    code: candidate.code,
    terminalId,
    staffId: deps.staffId(),
    createdAt,
  };
  const opId = uuidv7();

  await db.transaction(
    "rw",
    db.wifiVouchers,
    db.wifiVoucherIssuances,
    db.outbox,
    async () => {
      await db.wifiVouchers.update(candidate.code, { used: true });
      await db.wifiVoucherIssuances.add(issuance);
      await db.outbox.put({
        opId,
        type: "wifi_voucher.issue",
        payload: issuance,
        createdAt,
        terminalId,
        attempts: 0,
        state: "pending",
        lastError: null,
        nextAttemptAt: null,
      });
    },
  );

  return { outcome: "issued", code: candidate.code };
}
